import React, { useEffect, useRef, useState } from "react";
import { getAppLinks } from "../config/appLinks";

/**
 * AppDownloadQR — reusable "scan to get the app" cards for dashboard screens.
 *
 * Usage: <AppDownloadQR apps={["rider-android"]} title="Get the rider app" />
 *   - apps: keys from config/appLinks.js (omit for all apps)
 *   - title: optional heading; compact: smaller cards for tight sidebars
 *
 * QR rendering uses the qrcodejs library CDN-loaded once per session — the same
 * pattern the ticketing scanner uses for html5-qrcode (the dashboard bundles no
 * QR library). If the CDN is unreachable the card degrades to a plain link, so
 * the screen never breaks offline.
 */
const CDN = "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";

let qrLoader = null;
const loadQrLib = () => {
  if (window.QRCode) return Promise.resolve(true);
  if (!qrLoader) {
    qrLoader = new Promise((resolve) => {
      const s = document.createElement("script");
      s.src = CDN;
      s.async = true;
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
  }
  return qrLoader;
};

const QrBox = ({ url, size }) => {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    loadQrLib().then((ok) => {
      if (!alive) return;
      if (!ok || !window.QRCode || !ref.current) { setFailed(true); return; }
      ref.current.innerHTML = "";
      // eslint-disable-next-line no-new
      new window.QRCode(ref.current, { text: url, width: size, height: size, correctLevel: window.QRCode.CorrectLevel.M });
    });
    return () => { alive = false; };
  }, [url, size]);
  if (failed) return null;
  return <div ref={ref} style={{ width: size, height: size }} />;
};

const AppDownloadQR = ({ apps, title = "Get the apps", compact = false }) => {
  const links = getAppLinks(apps);
  if (!links.length) return null;
  const size = compact ? 84 : 112;
  return (
    <div style={{ margin: "14px 0" }}>
      {title && (
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8a9ab0", marginBottom: 8 }}>
          {title}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {links.map((app) => (
          <a
            key={app.key}
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex", alignItems: "center", gap: 12, textDecoration: "none",
              background: "#fff", border: "1px solid #e4e8ef", borderRadius: 12,
              padding: compact ? "10px 12px" : "12px 14px",
              boxShadow: "0 1px 4px rgba(16,42,67,0.06)",
            }}
          >
            <QrBox url={app.url} size={size} />
            <div style={{ minWidth: 120 }}>
              <div style={{ fontWeight: 800, fontSize: compact ? 13 : 14, color: "#1a2a3a" }}>{app.name}</div>
              <div style={{ fontSize: 11.5, color: "#6b7c93", marginTop: 2 }}>{app.audience}</div>
              <span style={{
                display: "inline-block", marginTop: 6, fontSize: 10.5, fontWeight: 700,
                letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 20,
                background: app.platform === "iOS" ? "#eef1f5" : "#e8f5e9",
                color: app.platform === "iOS" ? "#4a5568" : "#2e7d32",
                border: `1px solid ${app.platform === "iOS" ? "#e4e8ef" : "#c8e6c9"}`,
              }}>
                {app.platform}
              </span>
              <div style={{ fontSize: 10, color: "#9aacbf", marginTop: 6 }}>Scan or tap to open the store</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default AppDownloadQR;
